import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { NavLink } from 'react-router-dom'
import { ChevronRight, X, Printer, Send } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDateTime } from '../lib/utils'
import { PosVoidModal, POS_VOID_REASON_LABELS } from '../components/PosVoidModal'
import { SendReceiptModal } from '../components/SendReceiptModal'
import CsvControls from '../components/CsvControls'
import {
  JofotaraTriageCard, JofotaraRowChip,
  type JofotaraStatus,
} from '../components/JofotaraTriage'

// Dedicated **Sales (POS)** admin page — narrower than the mixed-feed
// `/orders` page in two ways:
//
//   1. Backend channel scope (default `pos`) excludes customer-app
//      delivery orders. Scope toggle exposes:
//        - `pos` (walk-in in-store sales)
//        - `phone_in` (cashier-rung delivery orders)
//        - `cashier` (both — the "cashier till" scope used for
//          commission calcs across sale types)
//   2. UI surfaces POS-specific filters (cashier + date-range) that
//      don't make sense on the delivery feed.
//
// The detail drawer is intentionally compact — line-by-line breakdown +
// totals + the void action — vs the full delivery-tracking drawer on
// `/orders`. Voids hit `POST /admin/orders/:id/void-pos` via the shared
// PosVoidModal so a label-set change lands in one place. Void is only
// offered on walk-in POS rows (backend enforces channel='pos' on the
// void-pos endpoint); phone-in refunds go through the delivery order
// detail drawer on the /orders page instead.

interface PosSaleRow {
  id: string
  order_number: string
  status: string
  total_fils: number
  effective_payment_method?: string
  payment_method: string
  payment_status: string
  partially_refunded_at: string | null
  channel: string
  cashier_id: string | null
  full_name: string // 'Walk-in' when no customer attached
  phone: string
  created_at: string
  jofotara_status: string | null
  jofotara_invoice_id: string | null
  jofotara_void_status: string | null
  jofotara_void_invoice_id: string | null
}

interface Cashier {
  id: string
  full_name: string
  active: number
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '',          label: 'All statuses' },
  { value: 'delivered', label: 'Completed' },
  { value: 'cancelled', label: 'Voided' },
]

type ChannelScope = 'pos' | 'phone_in' | 'cashier'
const CHANNEL_OPTIONS: Array<{ value: ChannelScope; label: string; hint: string }> = [
  { value: 'pos',      label: 'In-store (POS)',    hint: 'Walk-in sales rung up at the till' },
  { value: 'phone_in', label: 'Phone-in delivery', hint: 'Delivery orders rung up by a cashier' },
  { value: 'cashier',  label: 'All till sales',    hint: 'In-store + phone-in (commission scope)' },
]

// Discount source filter — Nadine ask (2026-10-06) + Robert extension.
// 'all' = no narrowing. 'any' matches orders with any form of discount
// (code, till override, line, order-level, bundle, VAT exempt). 'code'
// surfaces the code picker. Till / line / order / bundle / vat_exempt
// narrow to that specific discount shape.
type DiscountFilter = '' | 'any' | 'code' | 'till' | 'line' | 'order' | 'bundle' | 'vat_exempt'
const DISCOUNT_FILTER_OPTIONS: Array<{ value: DiscountFilter; label: string }> = [
  { value: '',          label: 'All sales' },
  { value: 'any',       label: 'Any discount applied' },
  { value: 'code',      label: 'Discount code' },
  { value: 'till',      label: 'Till discount (manual)' },
  { value: 'line',      label: 'Per-line discount' },
  { value: 'order',     label: 'Per-order discount' },
  { value: 'bundle',    label: 'Bundle promotion' },
  { value: 'vat_exempt',label: 'VAT exempt' },
]

interface DiscountCodeRow {
  id: string
  code: string
  type: 'percentage' | 'fixed' | 'free_delivery'
  value: number
  times_used: number
}

export default function PosSales() {
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [channel, setChannel] = useState<ChannelScope>('pos')
  const [cashierId, setCashierId] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [jofotaraStatus, setJofotaraStatus] = useState('')
  const [discountFilter, setDiscountFilter] = useState<DiscountFilter>('')
  const [discountCodeId, setDiscountCodeId] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const params = useMemo(() => {
    const p = new URLSearchParams()
    p.set('channel', channel)
    if (search) p.set('search', search)
    if (status) p.set('status', status)
    if (cashierId) p.set('cashier_id', cashierId)
    if (fromDate) p.set('from_date', fromDate)
    if (toDate) p.set('to_date', toDate)
    if (jofotaraStatus) p.set('jofotara_status', jofotaraStatus)
    if (discountFilter) p.set('discount_filter', discountFilter)
    if (discountFilter === 'code' && discountCodeId) p.set('discount_code_id', discountCodeId)
    p.set('sort', 'created_at')
    p.set('order', 'desc')
    p.set('page', String(page))
    return p
  }, [search, status, channel, cashierId, fromDate, toDate, jofotaraStatus, discountFilter, discountCodeId, page])

  // Export query mirrors the on-screen filters (minus the paging + sort
  // knobs — the CSV is always the full result set) so operators pulling
  // commission reports get exactly what they filtered to.
  const exportQuery = useMemo(() => {
    const p = new URLSearchParams()
    p.set('channel', channel)
    if (search) p.set('search', search)
    if (status) p.set('status', status)
    if (cashierId) p.set('cashier_id', cashierId)
    if (fromDate) p.set('from_date', fromDate)
    if (toDate) p.set('to_date', toDate)
    if (jofotaraStatus) p.set('jofotara_status', jofotaraStatus)
    if (discountFilter) p.set('discount_filter', discountFilter)
    if (discountFilter === 'code' && discountCodeId) p.set('discount_code_id', discountCodeId)
    return `?${p.toString()}`
  }, [channel, search, status, cashierId, fromDate, toDate, jofotaraStatus, discountFilter, discountCodeId])

  const { data, isError } = useQuery<{ orders: PosSaleRow[]; total: number; limit: number }>({
    queryKey: ['pos-sales', search, status, channel, cashierId, fromDate, toDate, jofotaraStatus, discountFilter, discountCodeId, page],
    queryFn: () => api.get(`/orders?${params.toString()}`).then(r => r.data),
  })

  // Lazy-load the discount code list only when the Discount code sub-filter
  // is selected — the admin/discounts list is paginated server-side and
  // we don't need it until the picker opens.
  const { data: discountCodeData } = useQuery<{ codes: DiscountCodeRow[] }>({
    queryKey: ['admin-discount-codes-for-filter'],
    queryFn: () => api.get('/discounts').then(r => r.data),
    enabled: discountFilter === 'code',
  })

  const { data: cashierData } = useQuery<{ cashiers: Cashier[] }>({
    queryKey: ['cashiers-for-filter'],
    queryFn: () => api.get('/cashiers').then(r => r.data),
  })

  const totalPages = Math.ceil((data?.total ?? 0) / (data?.limit ?? 50))

  function resetPage() { setPage(1) }

  const channelHint = CHANNEL_OPTIONS.find(o => o.value === channel)?.hint ?? ''

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold text-white">Sales (POS)</h1>
        <div className="flex items-center gap-3">
          <BulkJofotaraRetryButton />
          <CsvControls
            basePath="/orders"
            exportFilename={`sales-${channel}-${new Date().toISOString().slice(0, 10)}.csv`}
            exportOnly
            exportQuery={exportQuery}
          />
          <p className="text-xs text-gray-500">{channelHint}</p>
        </div>
      </div>

      {/* Tab strip — mirrors the strip on /pos-sales-detail so ops can
          flip between the summary + detail reports without leaving the
          Sales (POS) area. Haitham ask 2026-07-27. */}
      <div className="flex items-center gap-1 border-b border-white/5 mb-5">
        <NavLink
          to="/pos-sales"
          end
          className="px-4 py-2 text-sm font-medium text-white border-b-2 border-[#C41E3A]"
        >
          Sales Summary
        </NavLink>
        <NavLink
          to="/pos-sales-detail"
          className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-white border-b-2 border-transparent"
        >
          Sales Detail
        </NavLink>
      </div>

      {isError && (
        <div className="mb-4 text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">
          Failed to load sales. Check your connection or try refreshing.
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-5 items-end">
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Scope</label>
          <select
            value={channel}
            onChange={e => { setChannel(e.target.value as ChannelScope); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            title="Which sale types to include"
          >
            {CHANNEL_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Search</label>
          <input
            value={search}
            onChange={e => { setSearch(e.target.value); resetPage() }}
            placeholder="Order #, name, phone…"
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] w-56"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Status</label>
          <select
            value={status}
            onChange={e => { setStatus(e.target.value); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          >
            {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Cashier</label>
          <select
            value={cashierId}
            onChange={e => { setCashierId(e.target.value); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          >
            <option value="">All cashiers</option>
            {cashierData?.cashiers
              ?.filter(c => c.active)
              .map(c => <option key={c.id} value={c.id}>{c.full_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">From</label>
          <input
            type="date"
            value={fromDate}
            onChange={e => { setFromDate(e.target.value); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">To</label>
          <input
            type="date"
            value={toDate}
            onChange={e => { setToDate(e.target.value); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          />
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">JoFotara</label>
          <select
            value={jofotaraStatus}
            onChange={e => { setJofotaraStatus(e.target.value); resetPage() }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            title="Filter by JoFotara submission state — matches the sale-side OR the void-side credit-note"
          >
            <option value="">All</option>
            <option value="accepted">Accepted</option>
            <option value="submitted">Submitted</option>
            <option value="pending">Pending</option>
            <option value="retrying">Retrying</option>
            <option value="rejected">Rejected</option>
            <option value="skipped">Skipped</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Discount</label>
          <select
            value={discountFilter}
            onChange={e => {
              const v = e.target.value as DiscountFilter
              setDiscountFilter(v)
              if (v !== 'code') setDiscountCodeId('')
              resetPage()
            }}
            className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            title="Narrow to sales with a specific discount shape"
          >
            {DISCOUNT_FILTER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        {discountFilter === 'code' && (
          <div>
            <label className="block text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-1">Code</label>
            <select
              value={discountCodeId}
              onChange={e => { setDiscountCodeId(e.target.value); resetPage() }}
              className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            >
              <option value="">Any code</option>
              {(discountCodeData?.codes ?? []).map(c => {
                const label = c.type === 'percentage'
                  ? `${c.code} — ${c.value}%`
                  : c.type === 'fixed'
                    ? `${c.code} — JD ${(c.value / 1000).toFixed(3)}`
                    : `${c.code} — Free delivery`
                return <option key={c.id} value={c.id}>{label}</option>
              })}
            </select>
          </div>
        )}
        {(search || status || cashierId || fromDate || toDate || jofotaraStatus || discountFilter) && (
          <button
            type="button"
            onClick={() => {
              setSearch(''); setStatus(''); setCashierId(''); setFromDate(''); setToDate('');
              setJofotaraStatus(''); setDiscountFilter(''); setDiscountCodeId(''); resetPage()
            }}
            className="text-xs text-gray-500 hover:text-gray-300 px-3 py-2"
          >
            Clear filters
          </button>
        )}
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              <th className="px-4 py-3 w-6" />
              <th className="px-4 py-3 text-left font-medium">Order</th>
              <th className="px-4 py-3 text-left font-medium">Customer</th>
              <th className="px-4 py-3 text-left font-medium">Cashier</th>
              <th className="px-4 py-3 text-left font-medium">Total</th>
              <th className="px-4 py-3 text-left font-medium">Payment</th>
              <th className="px-4 py-3 text-left font-medium">Status</th>
              <th className="px-4 py-3 text-left font-medium">JoFotara</th>
              <th className="px-4 py-3 text-left font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {(data?.orders ?? []).length === 0 ? (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-gray-600 text-sm">No sales match these filters.</td></tr>
            ) : data?.orders.map(o => (
              <tr
                key={o.id}
                className="border-b border-[#222] hover:bg-white/2 transition-colors cursor-pointer"
                onClick={() => setSelectedId(o.id)}
              >
                <td className="pl-4 pr-0 py-3 text-gray-600"><ChevronRight size={14} /></td>
                <td className="px-4 py-3 font-mono text-xs text-gray-300">{o.order_number}</td>
                <td className="px-4 py-3">
                  <p className="text-white">{o.full_name}</p>
                  {o.phone && <p className="text-gray-500 text-xs">{o.phone}</p>}
                </td>
                <td className="px-4 py-3 text-gray-400 text-xs">
                  <CashierName cashierId={o.cashier_id} cashiers={cashierData?.cashiers} />
                </td>
                <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{formatCurrency(o.total_fils)}</td>
                <td className="px-4 py-3 text-gray-400 text-xs capitalize">
                  {(o.effective_payment_method ?? o.payment_method)?.replace(/_/g, ' ')}
                </td>
                <td className="px-4 py-3">
                  <PosStatusChip row={o} />
                </td>
                <td className="px-4 py-3">
                  <JofotaraRowChip
                    saleStatus={o.jofotara_status as JofotaraStatus}
                    voidStatus={o.jofotara_void_status as JofotaraStatus}
                  />
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(o.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2e2e2e] text-xs text-gray-500">
            <p>Page {page} of {totalPages} · {data?.total ?? 0} sale{data?.total === 1 ? '' : 's'}</p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1 rounded border border-[#2e2e2e] disabled:opacity-40 hover:bg-white/5"
              >
                Prev
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 rounded border border-[#2e2e2e] disabled:opacity-40 hover:bg-white/5"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <PosSaleDrawer
          id={selectedId}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  )
}

function CashierName({ cashierId, cashiers }: { cashierId: string | null; cashiers?: Cashier[] }) {
  if (!cashierId) return <span className="text-gray-600">—</span>
  const c = cashiers?.find(x => x.id === cashierId)
  return <span>{c?.full_name ?? <span className="text-gray-600 font-mono">{cashierId.slice(0, 8)}…</span>}</span>
}

function PosStatusChip({ row }: { row: PosSaleRow }) {
  if (row.status === 'cancelled') {
    return <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-red-500/15 text-red-400">Voided</span>
  }
  if (row.payment_status === 'refunded' && row.partially_refunded_at) {
    return <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-blue-500/15 text-blue-300">Refunded</span>
  }
  if (row.partially_refunded_at) {
    return <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-amber-500/15 text-amber-300">Partial refund</span>
  }
  return <span className="inline-block text-xs px-2 py-0.5 rounded-full font-medium bg-green-500/15 text-green-400">Completed</span>
}

// ── Drawer ──────────────────────────────────────────────────────────

interface SaleDetail {
  order: {
    id: string
    order_number: string
    channel: string
    status: string
    cashier_id: string | null
    cashier_name: string | null
    full_name: string
    user_phone: string
    subtotal_fils: number
    discount_amount_fils: number
    vat_exempt_amount_fils: number
    bundle_discount_fils: number
    delivery_fee_fils: number
    tip_fils: number
    total_fils: number
    payment_method: string
    effective_payment_method?: string
    tendered_fils: number | null
    change_fils: number | null
    drops_earned: number
    loyalty_points_earned: number
    store_credit_applied_fils: number
    gift_card_applied_fils: number
    created_at: string
    voided_at: string | null
    void_reason_code: string | null
    void_reason: string | null
    voided_by_admin_email: string | null
    partially_refunded_at: string | null
    // Margin fields (added 2026-07-12) — see Orders.tsx for the shape rationale.
    cogs_fils?: number
    margin_fils?: number
    margin_pct?: number | null
    // JoFotara sale-side + void-side state
    jofotara_status: JofotaraStatus
    jofotara_invoice_id: string | null
    jofotara_uuid: string | null
    jofotara_submitted_at: string | null
    jofotara_attempts: number | null
    jofotara_response_json: string | null
    jofotara_void_status: JofotaraStatus
    jofotara_void_invoice_id: string | null
    jofotara_void_uuid: string | null
    jofotara_void_submitted_at: string | null
    jofotara_void_attempts: number | null
    jofotara_void_response_json: string | null
  }
  items: Array<{
    id: string; name: string; quantity: number; unit_price_fils: number
  }>
  // Bundles applied to this sale — one entry per applied bundle instance.
  // Snapshot names come from `order_bundles.snapshot_json`, so they render
  // correctly even after the rule was later edited or deactivated.
  applied_bundles?: Array<{
    id: string
    slug: string
    kind: 'fixed' | 'bxgy'
    name_en: string
    name_ar: string
    discount_fils: number
  }>
}

function PosSaleDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [voidModalOpen, setVoidModalOpen] = useState(false)
  const [voidError, setVoidError] = useState<string | null>(null)
  const [sendReceiptOpen, setSendReceiptOpen] = useState(false)

  const { data, isLoading, isError } = useQuery<SaleDetail>({
    queryKey: ['pos-sale-detail', id],
    queryFn: () => api.get(`/orders/${id}`).then(r => r.data),
  })

  const voidPos = useMutation({
    mutationFn: ({ reason_code, reason_text }: { reason_code: string; reason_text?: string }) =>
      api.post(`/orders/${id}/void-pos`, { reason_code, reason_text }).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pos-sales'] })
      qc.invalidateQueries({ queryKey: ['pos-sale-detail', id] })
      setVoidModalOpen(false)
    },
    onError: (e: unknown) => {
      const msg = e instanceof Error ? e.message : String(e)
      setVoidError(msg)
    },
  })

  const o = data?.order

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#1a1a1a] border-l border-[#2e2e2e] overflow-y-auto">
        <div className="sticky top-0 z-10 bg-[#1a1a1a] border-b border-[#2e2e2e] px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-white font-mono text-sm">{o?.order_number ?? '…'}</p>
            <p className="text-gray-500 text-xs">{o ? formatDateTime(o.created_at) : ''}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/5 text-gray-400">
            <X size={18} />
          </button>
        </div>

        {isLoading && <p className="px-5 py-6 text-gray-500 text-sm">Loading…</p>}
        {isError && <p className="px-5 py-6 text-red-400 text-sm">Failed to load sale.</p>}

        {o && (
          <div className="px-5 py-4 space-y-5">
            <DetailKV k="Customer" v={o.full_name + (o.user_phone ? ` · ${o.user_phone}` : '')} />
            <DetailKV k="Cashier"  v={o.cashier_name ?? '—'} />

            <div>
              <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500 mb-2">Items</p>
              <div className="space-y-1.5 text-xs">
                {data?.items?.map((it, i) => (
                  <div key={i} className="flex justify-between gap-3 text-gray-300">
                    <span className="truncate">{it.quantity}× {it.name}</span>
                    <span className="text-gray-400 whitespace-nowrap">{formatCurrency(it.unit_price_fils * it.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[#2e2e2e] pt-3 space-y-1.5 text-xs">
              <KV k="Subtotal" v={formatCurrency(o.subtotal_fils)} />
              {o.discount_amount_fils > 0 && <KV k="Discount" v={`−${formatCurrency(o.discount_amount_fils)}`} positive />}
              {o.vat_exempt_amount_fils > 0 && <KV k="VAT exempt" v={`−${formatCurrency(o.vat_exempt_amount_fils)}`} positive />}
              {(o.bundle_discount_fils ?? 0) > 0 && (data.applied_bundles ?? []).map((b) => (
                <KV
                  key={b.id}
                  k={`Bundle: ${b.name_en}`}
                  v={`−${formatCurrency(b.discount_fils)}`}
                  positive
                />
              ))}
              {o.store_credit_applied_fils > 0 && <KV k="Store credit" v={`−${formatCurrency(o.store_credit_applied_fils)}`} positive />}
              {o.gift_card_applied_fils > 0 && <KV k="Gift card" v={`−${formatCurrency(o.gift_card_applied_fils)}`} positive />}
              {(o.delivery_fee_fils ?? 0) > 0 && <KV k="Delivery fee" v={formatCurrency(o.delivery_fee_fils)} muted />}
              {(o.tip_fils ?? 0) > 0 && <KV k="Tip (paid to driver)" v={formatCurrency(o.tip_fils)} muted />}
              <KV k="Total" v={formatCurrency(o.total_fils)} bold />
              <KV k="Payment" v={(o.effective_payment_method ?? o.payment_method).replace(/_/g, ' ')} muted />
              {o.payment_method === 'cod' && o.tendered_fils != null && (
                <>
                  <KV k="Tendered" v={formatCurrency(o.tendered_fils)} muted />
                  <KV k="Change"   v={formatCurrency(o.change_fils ?? 0)} muted />
                </>
              )}
              {o.drops_earned > 0 && <KV k="Drops earned" v={`+${o.drops_earned}`} muted />}
              {o.loyalty_points_earned > 0 && <KV k="Points earned" v={`+${o.loyalty_points_earned}`} muted />}
              {(o.cogs_fils ?? 0) > 0 && (
                <>
                  <div className="border-t border-[#2e2e2e] pt-2 mt-1" />
                  <KV k="COGS" v={formatCurrency(o.cogs_fils!)} muted />
                  <div className="flex justify-between">
                    <span className="text-gray-500">Gross margin</span>
                    <span className={(o.margin_fils ?? 0) >= 0 ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                      {formatCurrency(o.margin_fils!)}
                      {o.margin_pct != null && (
                        <span className="ml-1.5 text-gray-500 font-normal">({o.margin_pct.toFixed(1)}%)</span>
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>

            {/* Reprint — POS sales only, works even after void (the R2
                cache is busted on void so a post-void reprint reflects
                the cancelled status). Send receipt is shown next to it
                so support can re-dispatch to a different phone / email
                on customer request without printing paper. */}
            {o.channel === 'pos' && (
              <div className="border-t border-[#2e2e2e] pt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    const res = await api.get(`/orders/${o.id}/receipt.pdf`, { responseType: 'blob' })
                    const url = URL.createObjectURL(res.data as Blob)
                    window.open(url, '_blank')
                    setTimeout(() => URL.revokeObjectURL(url), 30_000)
                  }}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[#2a2a2a] border border-[#3a3a3a] text-xs font-semibold text-gray-200 hover:bg-[#333] transition-colors"
                >
                  <Printer size={12} /> Reprint
                </button>
                <button
                  type="button"
                  onClick={() => setSendReceiptOpen(true)}
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-md bg-[#2a2a2a] border border-[#3a3a3a] text-xs font-semibold text-gray-200 hover:bg-[#333] transition-colors"
                >
                  <Send size={12} /> Send receipt
                </button>
              </div>
            )}

            {/* Void chip vs Void action — walk-in POS sales only.
                Phone-in delivery orders refund via the delivery detail
                drawer on /orders (they can be dispatched, cancelled by
                driver, etc. — different lifecycle). */}
            {o.voided_at ? (
              <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs">
                <p className="font-semibold text-red-300 mb-1">Sale voided</p>
                <p className="text-gray-400">
                  {o.void_reason_code && `Reason: ${POS_VOID_REASON_LABELS[o.void_reason_code] ?? o.void_reason_code}`}
                  {o.void_reason && o.void_reason_code === 'other' && ` — ${o.void_reason}`}
                </p>
                <p className="text-gray-500 mt-1">{formatDateTime(o.voided_at)}{o.voided_by_admin_email && ` · ${o.voided_by_admin_email}`}</p>
              </div>
            ) : o.channel === 'pos' ? (
              <div>
                <button
                  type="button"
                  onClick={() => { setVoidError(null); setVoidModalOpen(true) }}
                  className="w-full px-3 py-2 rounded-md bg-red-500/10 border border-red-500/30 text-xs font-semibold text-red-300 hover:bg-red-500/20 transition-colors"
                >
                  Void this sale
                </button>
              </div>
            ) : (
              <div className="border-t border-[#2e2e2e] pt-3">
                <a
                  href={`/orders?search=${encodeURIComponent(o.order_number)}`}
                  className="block w-full text-center px-3 py-2 rounded-md bg-[#2a2a2a] border border-[#3a3a3a] text-xs font-medium text-gray-300 hover:bg-[#333] transition-colors"
                  title="Phone-in delivery orders are refunded from the delivery order drawer, not void-pos"
                >
                  Open in Orders to refund →
                </a>
              </div>
            )}

            {/* JoFotara triage cards — one per submission (sale + void) */}
            <JofotaraTriageCard
              kind="sale"
              orderId={o.id}
              status={o.jofotara_status}
              invoiceId={o.jofotara_invoice_id}
              uuid={o.jofotara_uuid}
              submittedAt={o.jofotara_submitted_at}
              attempts={o.jofotara_attempts}
              responseJson={o.jofotara_response_json}
              invalidateKeys={[['pos-sales'], ['pos-sale-detail', o.id]]}
            />
            {o.voided_at && (
              <JofotaraTriageCard
                kind="void"
                orderId={o.id}
                status={o.jofotara_void_status}
                invoiceId={o.jofotara_void_invoice_id}
                uuid={o.jofotara_void_uuid}
                submittedAt={o.jofotara_void_submitted_at}
                attempts={o.jofotara_void_attempts}
                responseJson={o.jofotara_void_response_json}
                invalidateKeys={[['pos-sales'], ['pos-sale-detail', o.id]]}
              />
            )}
          </div>
        )}

        {voidModalOpen && o && (
          <PosVoidModal
            orderNumber={o.order_number}
            isSubmitting={voidPos.isPending}
            errorMessage={voidError}
            onCancel={() => setVoidModalOpen(false)}
            onConfirm={(code, text) => voidPos.mutate({ reason_code: code, reason_text: text })}
          />
        )}
        {sendReceiptOpen && o && (
          <SendReceiptModal
            orderId={o.id}
            orderNumber={o.order_number}
            onClose={() => setSendReceiptOpen(false)}
          />
        )}
      </div>
    </div>
  )
}

function DetailKV({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <p className="text-[10px] font-medium uppercase tracking-wider text-gray-500">{k}</p>
      <p className="text-sm text-white">{v}</p>
    </div>
  )
}

function KV({ k, v, bold = false, muted = false, positive = false }: { k: string; v: string; bold?: boolean; muted?: boolean; positive?: boolean }) {
  return (
    <div className="flex justify-between">
      <span className={muted ? 'text-gray-500' : 'text-gray-400'}>{k}</span>
      <span className={`${bold ? 'font-semibold text-white' : positive ? 'text-green-400' : 'text-gray-300'}`}>{v}</span>
    </div>
  )
}


/// Page-level "Reset all rejected JoFotara submissions" button — only
/// visible when at least one rejected row exists in the current feed
/// (cheap check via the query data). Confirms before firing.
function BulkJofotaraRetryButton() {
  const qc = useQueryClient()
  const [confirming, setConfirming] = useState(false)
  const [result, setResult] = useState<{ sale_reset: number; void_reset: number } | null>(null)

  const retryAll = useMutation({
    mutationFn: () => api.post('/orders/jofotara-retry-rejected', { channel: 'pos' }).then(r => r.data),
    onSuccess: (data: { sale_reset?: number; void_reset?: number }) => {
      setResult({ sale_reset: data.sale_reset ?? 0, void_reset: data.void_reset ?? 0 })
      setConfirming(false)
      qc.invalidateQueries({ queryKey: ['pos-sales'] })
    },
  })

  if (result) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-green-400">
          Queued {result.sale_reset} sale + {result.void_reset} void submission{result.sale_reset + result.void_reset === 1 ? '' : 's'}
        </span>
        <button onClick={() => setResult(null)} className="text-[11px] text-gray-500 hover:text-gray-300 underline">
          Dismiss
        </button>
      </div>
    )
  }

  if (confirming) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-[11px] text-gray-400">Reset all rejected POS submissions?</span>
        <button
          onClick={() => retryAll.mutate()}
          disabled={retryAll.isPending}
          className="px-2.5 py-1 rounded bg-amber-500/15 border border-amber-500/40 text-[11px] font-semibold text-amber-300 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
        >
          {retryAll.isPending ? 'Resetting…' : 'Yes, reset'}
        </button>
        <button onClick={() => setConfirming(false)} className="text-[11px] text-gray-500 hover:text-gray-300">
          Cancel
        </button>
      </div>
    )
  }

  return (
    <button
      onClick={() => setConfirming(true)}
      className="px-2.5 py-1 rounded border border-[#2e2e2e] text-[11px] font-medium text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
      title="Reset all rejected JoFotara submissions (sale + void) to 'retrying' so the cron picks them up"
    >
      Retry rejected JoFotara
    </button>
  )
}

